import React from 'react'

import { TextClamp } from '../components/text/TextClamp'
import { clampArray } from '../foundations/slide-limits'
import { schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { PlotBox } from '../components/chart-segment/PlotBox'
import { ChartSegmentTitleSlot } from '../components/chart-segment/ChartSegmentTitleSlot'
import { registerSlide } from './registry'
import { BandLayout } from '../layouts/BandLayout'
import { presetSlot } from '../layouts/presetSlots'
import { SlotBullets, SlotHeadline, SlotNarrative, SlotPlot, type SlotPlotShape } from '../slots'
import { EditableSlideText } from './EditableSlideText'
import type { EditableSlideProps } from './contracts'
import type { ContinuousDensitySlideData } from './renderer-internal-types'
import {
  DensityBandLegend,
  DensityHistogramSlideChart,
  DensityView,
  continuousDensitySlideAdapter,
  densityView,
  legacyContinuousDensityFormatter,
  legacyDensityUnitLabel,
  readContinuousDensityPlot,
  resolveDensityView,
} from '../plots/density-histogram'

// legacy reader only: the narrative eyebrow is required, so a deck built today stamps it.
const LEGACY_EYEBROW = 'Continuous'

const GRAY = 'var(--slide-text-muted)'
const BLACK = 'var(--slide-text-primary)'
const BRAND_ACCENT = 'var(--slide-accent)'
const PANEL = 'var(--slide-card-bg)'

/**
 * What the panel draws (plots plan M). A slide built from M on carries the
 * `density-histogram` plot (the study figures' Total histogram, shared edges,
 * bin names and numbers in the deck language). A stored slide without it is
 * read by the legacy adapter and printed by its own rules, exactly as before,
 * including today's English fallbacks for chrome it never stored.
 */
const SLIDE_TYPE = 'continuous-density'
const HEADLINE = presetSlot(SLIDE_TYPE, 'headline').text!
const NARRATIVE = presetSlot(SLIDE_TYPE, 'narrative').text!
const BULLETS = presetSlot(SLIDE_TYPE, 'bullets').text!
const PLOT = presetSlot(SLIDE_TYPE, 'plot')

/**
 * The chart heading is the question, whole, beside the middle-50% key. Its line
 * budget (`x-limits.text.chartTitle.maxLines`) is what the band between the heading's
 * top and the plot's label row (`DENSITY_SLIDE_LABEL_ROW_TOP`) holds: 3 lines of 16px
 * at 1.2 end at 86.6px, above the 98px row. `ContinuousDensitySlide.test.tsx` asserts
 * the sum.
 */
export const CONTINUOUS_DENSITY_CHART_TITLE_TOP_PX = 29
export const CONTINUOUS_DENSITY_CHART_TITLE_FONT_SIZE_PX = 16
export const CONTINUOUS_DENSITY_CHART_TITLE_LINE_HEIGHT = 1.2
const CHART_TITLE_CLAMP = schemaBackedProseClampProps('chartTitle', SLIDE_TYPE, {
  lineHeight: String(CONTINUOUS_DENSITY_CHART_TITLE_LINE_HEIGHT),
})

function slideDensityView(props: ContinuousDensitySlideData): DensityView {
  const plot = readContinuousDensityPlot(props)
  if (plot) {
    const locale = typeof (props as { locale?: unknown }).locale === 'string' ? ((props as { locale?: string }).locale as string) : 'en'
    return resolveDensityView(plot.data, locale)
  }
  const stored = { ...props, bins: clampArray(props.bins || [], 'continuous-density', 'bins') }
  const legacy = densityView(continuousDensitySlideAdapter.read(stored).data, legacyContinuousDensityFormatter(stored))
  // legacy reader only: a slide without the stamped plot; the builder stamps these words in `displayLabels`.
  return {
    ...legacy,
    xAxisTitle: props.displayLabels?.xAxisTitle || legacyDensityUnitLabel(stored),
    yAxisTitle: legacy.yAxisTitle || 'Share of respondents',
    medianWord: legacy.medianWord || 'Median',
    meanWord: legacy.meanWord || 'Mean',
    bandWord: legacy.bandWord || 'Middle 50%',
  }
}

function ContinuousDensitySlideComponent(props: ContinuousDensitySlideData & EditableSlideProps) {
  // D17: `renderMode` is read here and forwarded to every clamp that offers a
  // truncation tooltip. A static export is the headless-browser PDF pass, where a Radix
  // trigger can never open, so those clamps have to fall back to the native `title`.
  const { editable, onEdit, renderMode } = props
  const resolvedActionTitle = sanitizeNarrativeText(props.actionTitle)
  const logoUrl = props.clientLogoUrl ?? null
  const view = React.useMemo(() => slideDensityView(props), [props])
  const series = view.series[0]
  const bins = series?.bins ?? []
  const statValue = (value: number | null | undefined) => view.formatter.value(value)

  return (
    <BandLayout
      preset={SLIDE_TYPE}
      eyebrow={props.eyebrow || LEGACY_EYEBROW}
      clientLogoUrl={logoUrl}
      testIds={{ layout: 'continuous-density-layout', band: 'continuous-density-summary' }}
      band={
        <>
          <SlotHeadline text={resolvedActionTitle || ''} maxLines={HEADLINE.maxLines} color={BLACK} editable={editable} onEdit={onEdit} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {props.actionSubtitle ? (
              <SlotNarrative
                value={props.actionSubtitle}
                clamp={{ minLines: 1, maxLines: NARRATIVE.maxLines, lineHeight: '1.5' }}
                color={BLACK}
                editable={editable}
                onEdit={onEdit}
                renderMode={renderMode}
              />
            ) : null}
            {props.insightBullets?.length ? (
              <SlotBullets
                bullets={props.insightBullets}
                fontSize={BULLETS.fontSize}
                gap={6}
                maxLines={BULLETS.maxLines}
                dotColor={BRAND_ACCENT}
                columnGap={10}
                color={BLACK}
                testId="continuous-density-insight-bullets"
                style={{ flexShrink: 0 }}
                editable={editable}
                onEdit={onEdit}
                renderMode={renderMode}
              />
            ) : null}
            <div data-testid="continuous-density-stat-row" style={{ display: 'grid', gridTemplateColumns: '200px 1fr', columnGap: 24, height: 59, flexShrink: 0 }}>
              {[
                ['continuous-density-stat-mean-value', view.meanWord ?? '', statValue(series?.mean)],
                ['continuous-density-stat-median-value', view.medianWord ?? '', view.formatter.median(series?.median)],
              ].map(([testId, label, value]) => (
                <div key={label} style={{ minWidth: 0 }}>
                  <TextClamp minLines={1} maxLines={1} lineHeight="1.2" style={{ fontSize: 13, color: GRAY }}>
                    {label}
                  </TextClamp>
                  <TextClamp data-testid={testId} minLines={1} maxLines={1} lineHeight="1.2" style={{ marginTop: 4, fontSize: 32, fontWeight: 500, color: BRAND_ACCENT }}>
                    {value}
                  </TextClamp>
                </div>
              ))}
            </div>
          </div>
        </>
      }
    >
      <SlotPlot shape={PLOT.shape as SlotPlotShape} x={PLOT.rect!.x} y={PLOT.rect!.y} testId="continuous-density-panel" style={{ borderRadius: 16, background: PANEL, overflow: 'hidden' }}>
          <PlotBox
            slide={props as unknown as Record<string, unknown>}
            slideType="continuous-density"
            columns={bins.map((bin) => ({ id: bin.id, label: bin.label ?? '', min: bin.min, max: bin.max }))}
            title={props.chartTitle || props.title || ''}
          >
          {/* The question and the middle-50% key share one header row, clear of the
              chart-segment control, as on every chart slide (ChartSegmentTitleSlot). */}
          <ChartSegmentTitleSlot
            testId="continuous-density-chart-header"
            style={{ position: 'absolute', left: 31, top: CONTINUOUS_DENSITY_CHART_TITLE_TOP_PX, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}
          >
            <EditableSlideText
              data-testid="continuous-density-chart-title"
              value={props.chartTitle || props.title || ''}
              field="chartTitle"
              editable={editable}
              onEdit={onEdit}
              tooltipOnTruncate
              renderMode={renderMode}
              minLines={CHART_TITLE_CLAMP.minLines}
              maxLines={CHART_TITLE_CLAMP.maxLines}
              lineHeight={CHART_TITLE_CLAMP.lineHeight}
              style={{ flex: '1 1 0%', minWidth: 0, fontSize: CONTINUOUS_DENSITY_CHART_TITLE_FONT_SIZE_PX, fontWeight: 500, color: BLACK }}
            />
            <DensityBandLegend view={view} />
          </ChartSegmentTitleSlot>

          <DensityHistogramSlideChart view={view} renderMode={renderMode} />
          </PlotBox>
      </SlotPlot>
    </BandLayout>
  )
}

export const ContinuousDensitySlide = registerSlide('continuous-density', ContinuousDensitySlideComponent)
